import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import { useToast } from 'src/hooks/use-toast';
import {
  useAllTestGroups,
  useTestCollection,
} from 'src/services/api/queries/testQueries';
import {
  useCreateTestCollection,
  useUpdateTestCollection,
} from 'src/services/api/mutations/testMutations';
import { NewTestCollection } from '../NewTestCollection/NewTestCollection';

const TestCollectionForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const isEditMode = Boolean(id);

  const createCollection = useCreateTestCollection();
  const updateCollection = useUpdateTestCollection();

  const { data: groupsResponse, isLoading: groupsLoading } = useAllTestGroups({
    pageNo: 1,
    pageSize: 10,
    search: '',
    status: 'ACTIVE',
  });
  const allTestGroups = groupsResponse?.data?.items || [];

  // Only fetches in edit mode (enabled: !!id); refetched after a successful update.
  const {
    data: collectionResponse,
    isLoading: collectionLoading,
    refetch: refetchCollection,
  } = useTestCollection(id);
  const editingData = collectionResponse?.data || null;

  const loading = createCollection.isPending || updateCollection.isPending;
  const initialLoading = groupsLoading || (isEditMode && collectionLoading);

  const handleSubmit = async (formData) => {
    try {
      if (isEditMode) {
        await updateCollection.mutateAsync({ id, data: formData });
        toast({
          title: 'Success',
          description: 'Test collection updated successfully',
          variant: 'success',
        });
        // Refetch the data to get the updated collection
        await refetchCollection();
      } else {
        await createCollection.mutateAsync(formData);
        toast({
          title: 'Success',
          description: 'Test collection created successfully',
          variant: 'success',
        });
        // Navigate back to test management with collections tab active
        navigate('/tests', { state: { activeTab: 'collections' } });
      }
    } catch (error) {
      console.error('Failed to save test collection:', error);
      toast({
        title: error.message || 'Error',
        description:
          error.error?.[0]?.error ||
          'Failed to save test collection. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const breadcrumbs = [
    { label: 'Test Management', href: '/tests' },
    {
      label: 'Collections',
      href: '/tests',
      state: { activeTab: 'collections' },
    },
    {
      label: isEditMode ? 'Edit Collection' : 'New Collection',
      href: '#',
    },
  ];

  return (
    <div>
      <PageHeader breadcrumbs={breadcrumbs} />

      <PageContainer className="pb-0 space-y-4">
        {initialLoading ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-2 border-border border-t-primary mx-auto" />
              <p className="mt-4 text-[13px] text-muted-foreground">
                Loading...
              </p>
            </div>
          </div>
        ) : (
          <NewTestCollection
            onSubmit={handleSubmit}
            initialData={editingData}
            availableGroups={allTestGroups}
            loading={loading}
          />
        )}
      </PageContainer>
    </div>
  );
};

export default TestCollectionForm;
